import React, {useEffect, useState} from "react";
import Select from "react-select";
import countries from "../data/countries.json"; // Import the country list (legacy format)
import countriesWithCodes from "../data/countries-with-codes.json"; // Import the country list with codes
import states from "../data/states.json"; // Import the states list (now with codes)
import highSchools from "../data/high-schools.json"; // Import the high schools list with CEEB codes

const fieldOptions = [
    {value: "input", label: "Short Text"},
    {value: "student_bio", label: "Basic Info"},
    {value: "citizenship", label: "Citizenship"},
    {value: "country", label: "Country"},
    {value: "states", label: "State"},
    {value: "high_school", label: "High School"},
    {value: "select", label: "Dropdown"},
    {value: "date", label: "Date"},
    {value: "email", label: "Email"},
    {value: "radio", label: "Single Choice"},
    {value: "phone", label: "Phone"},
    {value: "display_text", label: "Display Text"},
];

const EditFieldModal = ({field, fieldIndex, onSave, onClose}) => {
    const [fieldType, setFieldType] = useState(fieldOptions[0]);
    const [fieldName, setFieldName] = useState("");
    const [required, setRequired] = useState(false);
    const [options, setOptions] = useState([]); // Will store objects with name/value pairs
    const [displayText, setDisplayText] = useState(""); // For display_text field type

    // Helper function to convert legacy options to new format
    const convertToNewFormat = (legacyOptions) => {
        if (!legacyOptions) return [];
        return legacyOptions.map(option => {
            if (typeof option === 'string') {
                return { name: option, value: option };
            } else if (option && typeof option === 'object' && option.name) {
                return { name: option.name, value: option.value || option.name };
            }
            return { name: "", value: "" };
        });
    };

    useEffect(() => {
        if (field) {
            setFieldType(fieldOptions.find((opt) => opt.value === field.type) || fieldOptions[0]);
            setFieldName(field.name || "");
            setRequired(field.required || false);
            
            // Convert options to new format and ensure default options are present
            let convertedOptions = convertToNewFormat(field.options || []);
            if (field.name === "Citizenship" && !convertedOptions.some(opt => opt.name.startsWith("Please select"))) {
                convertedOptions = [
                    { name: "Please select a country", value: "" },
                    ...convertedOptions
                ];
            } else if (field.name === "Country" && !convertedOptions.some(opt => opt.name.startsWith("Please select"))) {
                convertedOptions = [
                    { name: "Please select a country", value: "" },
                    ...convertedOptions
                ];
            } else if (field.name === "State" && !convertedOptions.some(opt => opt.name.startsWith("Please select"))) {
                convertedOptions = [
                    { name: "Please select a state", value: "" },
                    ...convertedOptions
                ];
            } else if (field.name === "High School" && !convertedOptions.some(opt => opt.name.startsWith("Please select"))) {
                convertedOptions = [
                    { name: "Please select a high school", value: "" },
                    ...convertedOptions
                ];
            }
            setOptions(convertedOptions);
            
            // Initialize display text for display_text fields
            if (field.type === "display_text") {
                setDisplayText(field.text || "");
            }
        }
    }, [field]);

    // Handle field type changes
    useEffect(() => {
        if (fieldType.value === "citizenship") {
            setFieldName("Citizenship");
            setRequired(true);
            setOptions([
                { name: "Please select a country", value: "" },
                ...countries.map((c) => ({ name: c.name, value: c.name }))
            ]);
        } else if (fieldType.value === "country") {
            setFieldName("Country");
            setRequired(true);
            setOptions([
                { name: "Please select a country", value: "" },
                ...countriesWithCodes.map((c) => ({ name: c.name, value: c.code }))
            ]);
        } else if (fieldType.value === "states") {
            setFieldName("State");
            setRequired(true);
            setOptions([
                { name: "Please select a state", value: "" },
                ...states.map((s) => ({ name: s.name, value: s.code }))
            ]);
        } else if (fieldType.value === "high_school") {
            setFieldName("High School");
            setRequired(true);
            setOptions([
                { name: "Please select a high school", value: "" },
                ...highSchools.map((school) => ({ name: school.name, value: school.code }))
            ]);
        }
    }, [fieldType]);

    const addOption = () => {
        setOptions([...options, { name: "", value: "" }]);
    };

    const updateOption = (index, field, value) => {
        const updatedOptions = [...options];
        updatedOptions[index] = { ...updatedOptions[index], [field]: value };
        setOptions(updatedOptions);
    };

    const updateOptionName = (index, name) => {
        const updatedOptions = [...options];
        updatedOptions[index] = { 
            name: name, 
            value: updatedOptions[index]?.value || name // Auto-populate value with name if empty
        };
        setOptions(updatedOptions);
    };
    // Student Bio predefined fields
    const studentBioFields = [
        {name: "First Name", type: "input", required: true},
        {name: "Last Name", type: "input", required: true},
        {name: "Date of Birth", type: "date", required: true},
        {name: "Email", type: "email", required: true},
        {name: "Phone", type: "phone", required: false},
    ];
    const saveField = () => {
        if (!fieldName.trim()) {
            alert("Field Name is required");
            return;
        }

        if (fieldType.value === "display_text") {
            if (!displayText.trim()) {
                alert("Display Text is required");
                return;
            }
            const updatedField = {
                index: fieldIndex,
                name: fieldName,
                type: "display_text",
                required: false, // Display text fields are never required
                text: displayText,
            };
            onSave([updatedField]);
        } else {
            // Process options to ensure they have both name and value
            let processedOptions = null;
            if (fieldType.value === "select" || fieldType.value === "radio" || fieldType.value === "citizenship" || fieldType.value === "country" || fieldType.value === "states" || fieldType.value === "high_school") {
                processedOptions = options.map(option => {
                    if (typeof option === 'string') {
                        // Legacy format - convert to new format
                        return { name: option, value: option };
                    } else if (option && typeof option === 'object') {
                        // New format - ensure both name and value exist
                        return {
                            name: option.name || "",
                            value: option.value || option.name || ""
                        };
                    }
                    return { name: "", value: "" };
                }).filter(option => option.name.trim() !== ""); // Remove empty options
            } else {
                // For non-select/radio fields, preserve original options or set to empty array
                processedOptions = field.options || [];
            }

            const updatedField = {
                index: fieldIndex,
                name: fieldName,
                type: fieldType.value === "citizenship" || fieldType.value === "country" || fieldType.value === "states" || fieldType.value === "high_school" ? "select" : fieldType.value,
                required,
                options: processedOptions,
            };

            onSave([updatedField]);
        }
    };

    return (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "5px",
                    width: "400px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
                }}
            >
                <h2>Edit Field</h2>

                <div className="form-group">
                    <label>Field Type:</label>
                    <Select options={fieldOptions} value={fieldType} onChange={setFieldType} isSearchable={false}/>
                </div>
                <div className="form-group">
                    <label>Field Name:</label>
                    <input
                        type="text"
                        className="form-control"
                        value={fieldName}
                        onChange={(e) => setFieldName(e.target.value)}
                        disabled={fieldType.value === "student_bio"}
                    />
                </div>
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={required}
                            onChange={(e) => setRequired(e.target.checked)}
                            disabled={fieldType.value === "citizenship" || fieldType.value === "country" || fieldType.value === "states" || fieldType.value === "high_school" || fieldType.value === "student_bio"}
                        />
                        Required
                    </label>
                </div>
                {(fieldType.value === "select" || fieldType.value === "radio") && (
                    <div className="form-group">
                        <label>Options:</label>
                        <div
                            style={{
                                maxHeight: "200px", // Increased height for two fields
                                overflowY: "auto", // Enable vertical scrolling
                                padding: "5px",
                                borderRadius: "5px",
                                border: "1px solid #ddd",
                            }}
                        >
                            {options.map((option, index) => (
                                <div
                                    key={index} 
                                    style={{
                                        display: "flex", 
                                        alignItems: "center", 
                                        marginBottom: "10px",
                                        padding: "5px",
                                        border: "1px solid #eee",
                                        borderRadius: "3px"
                                    }}
                                >
                                    <div style={{ flex: 1, marginRight: "10px" }}>
                                        <label style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
                                            Display Name:
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g., Nevada"
                                            value={option.name || ""}
                                            onChange={(e) => updateOptionName(index, e.target.value)}
                                            style={{ fontSize: "12px" }}
                                        />
                                    </div>
                                    <div style={{ flex: 1, marginRight: "10px" }}>
                                        <label style={{ fontSize: "12px", color: "#666", marginBottom: "2px" }}>
                                            System Value:
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g., NE"
                                            value={option.value || ""}
                                            onChange={(e) => updateOption(index, "value", e.target.value)}
                                            style={{ fontSize: "12px" }}
                                        />
                                    </div>
                                    <button 
                                        className="btn btn-default clone-btn-right delete" 
                                        type="button"
                                        onClick={() => setOptions(options.filter((_, i) => i !== index))}
                                        style={{ marginTop: "20px" }}
                                    >
                                        -
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="btn btn-sm btn-info" onClick={addOption}>
                                + Add Option
                            </button>
                        </div>
                        <small style={{ color: "#666", display: "block", marginTop: "5px" }}>
                            💡 <strong>Display Name</strong> is what users see. <strong>System Value</strong> is what gets sent to external systems.
                            <br/>System Value automatically copies from Display Name. Once manually edited, auto-copy stops for that option.
                        </small>
                    </div>
                )}
                                        {(fieldType.value === "citizenship" || fieldType.value === "country") && (
                    <div className="form-group">
                        <label>Country List:</label>
                        <input type="text" className="form-control" value="Predefined" disabled/>
                    </div>
                )}
                {fieldType.value === "states" && (
                    <div className="form-group">
                        <label>State List:</label>
                        <input type="text" className="form-control" value="Predefined" disabled/>
                    </div>
                )}
                {fieldType.value === "high_school" && (
                    <div className="form-group">
                        <label>High School List:</label>
                        <input type="text" className="form-control" value="Predefined" disabled/>
                    </div>
                )}
                {fieldType.value === "student_bio" && (
                    <div className="form-group">
                        <label>Fields in Basic Info:</label>
                        <ul style={{listStyleType: "none", padding: 0, fontSize: "14px", color: "#555"}}>
                            {studentBioFields.map((field, index) => (
                                <li key={index}>
                                    <strong>{field.name}</strong> ({field.type}) {field.required ? "(Required)" : "(Optional)"}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {fieldType.value === "display_text" && (
                    <div className="form-group">
                        <label>Display Text:</label>
                        <textarea
                            className="form-control"
                            value={displayText}
                            onChange={(e) => setDisplayText(e.target.value)}
                            rows={4}
                            placeholder="Enter the text to display to users. Use [link]Link Text|https://example.com[/link] for clickable links."
                        />
                        <div style={{marginTop: "8px", fontSize: "12px", color: "#666"}}>
                            💡 This field displays text to users but won't be included in form submissions. 
                            To add clickable links, use this format: [link]Link Text|https://example.com[/link]
                        </div>
                    </div>
                )}
                <div style={{marginTop: "20px", textAlign: "right"}}>
                    <button type="button" className="btn btn-primary" onClick={saveField} style={{marginRight: "10px"}}>
                        Update
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditFieldModal;